import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import { normalizeTimeZone } from '@mangovoice/constants'

import type { SystemSettingsApi } from '../api/systemSettingsApi.types'
import type { SystemSettingsUpdatePayload } from '../types'

const QUERY_KEYS = {
  settings: (pbxId: number) => ['systemSettings', pbxId] as const,
  phoneNumbers: (pbxId: number) =>
    ['systemSettings', 'phoneNumbers', pbxId] as const,
  extensions: (pbxId: number) =>
    ['systemSettings', 'extensions', pbxId] as const,
  virtualFaxMachines: (pbxId: number) =>
    ['systemSettings', 'virtualFaxMachines', pbxId] as const,
  playlists: (pbxId: number) => ['systemSettings', 'playlists', pbxId] as const,
}

export const useGetSystemSettings = (
  api: SystemSettingsApi,
  pbxId: number | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.settings(pbxId ?? 0),
    queryFn: () => api.getSystemSettings(pbxId!),
    enabled: !!pbxId,
    retry: false,
  })
}

export const useUpdateSystemSettings = (
  api: SystemSettingsApi,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: SystemSettingsUpdatePayload) =>
      api.updateSystemSettings(pbxId!, payload),
    onSuccess: () => {
      if (pbxId) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.settings(pbxId),
        })
      }
    },
  })
}

export const useGetPhoneNumbers = (
  api: SystemSettingsApi,
  pbxId: number | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.phoneNumbers(pbxId ?? 0),
    queryFn: () => api.getPhoneNumbers(pbxId!),
    enabled: !!pbxId,
    retry: false,
  })
}

export const useGetExtensions = (
  api: SystemSettingsApi,
  pbxId: number | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.extensions(pbxId ?? 0),
    queryFn: () => api.getExtensions(pbxId!),
    enabled: !!pbxId,
    retry: false,
  })
}

export const useGetVirtualFaxMachines = (
  api: SystemSettingsApi,
  pbxId: number | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.virtualFaxMachines(pbxId ?? 0),
    queryFn: () => api.getVirtualFaxMachines(pbxId!),
    enabled: !!pbxId,
    retry: false,
  })
}

export const useGetPlaylists = (
  api: SystemSettingsApi,
  pbxId: number | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.playlists(pbxId ?? 0),
    queryFn: () => api.getPlaylists(pbxId!),
    enabled: !!pbxId,
    retry: false,
  })
}

export const usePbxTimeZone = (
  api: SystemSettingsApi,
  pbxId: number | undefined,
) => {
  const {
    data: systemSettings,
    isLoading,
    isError,
  } = useGetSystemSettings(api, pbxId)

  const pbxTimeZone = useMemo(() => {
    const raw = systemSettings?.default_time_zone?.trim()
    if (!raw) return undefined
    return normalizeTimeZone(raw)
  }, [systemSettings?.default_time_zone])

  return { pbxTimeZone, isLoading, isError }
}
